import React, { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { IBlockSettings, ICalendarPosition, ISortedGroupedSchedule, ISortedShift } from '@/types/schedule';
import type { BaseShiftProps } from '@/components/agent/my-schedule';
import type { IWeekDay } from '@/types/calendar';
import type { RootState } from '@/store';
import type { Hue } from '@/types/styles';

import { SchedulesContainerLabels, ShiftsContainer, ShiftTooltip } from '@/components/agent/my-schedule';
import { colors, otherColors } from '@/styles/colors';
import { checkOverlap, classList, getBlockSettings, getCalendarPosition } from '@/helpers';
import { useAppDispatch, useAppSelector, useMouseClickOutside, useTimezone } from '@/hooks';
import { HOUR_IN_PX, PADDING_SCHEDULE } from '@/config/variables';
import { ShiftAssignStatus } from '@/store/enums';
import { toggleDisplayDetailsOnGrouppedSchedule } from '@/store/slices/scheduleSlice';
import {
  selectScheduleColors,
  selectScheduleIdsToShowDetailsFor,
  selectSchedulesList,
  selectSelectedSchedulesIds,
} from '@/store/selectors/scheduleSelectors';

const schedulesContainerSelector = createSelector(
  [
    selectSchedulesList,
    selectScheduleColors,
    selectSelectedSchedulesIds,
    selectScheduleIdsToShowDetailsFor,
    (_: RootState, scheduleId: number) => scheduleId,
  ],
  (schedulesList, scheduleColors, selectedSchedulesIds, scheduleIdsToShowDetailsFor, scheduleId) => ({
    schedulesList,
    scheduleColors,
    selectedSchedulesIds,
    areScheduleDetailsDisplayed: scheduleIdsToShowDetailsFor.includes(scheduleId),
  }),
);

export interface IProps {
  shift: ISortedShift<ISortedGroupedSchedule>;
  modal?: BaseShiftProps['modal'];
  onClick: (() => void) | undefined;
  day: IWeekDay;
}

const SchedulesContainer = ({ shift, modal, onClick, day }: IProps): React.ReactElement => {
  const { schedulesList, scheduleColors, selectedSchedulesIds, areScheduleDetailsDisplayed } = useAppSelector(state =>
    schedulesContainerSelector(state, shift.scheduleId),
  );
  const { timezoneName } = useTimezone();
  const dispatch = useAppDispatch();

  const node = useRef<HTMLDivElement>(null);

  useMouseClickOutside(node, (event: MouseEvent) => {
    if (!areScheduleDetailsDisplayed) return;
    const path: HTMLDivElement[] = (event as any).path || (event.composedPath && event.composedPath()) || [];
    const isClickedScheduleFocused = Boolean(path.find(item => item.classList?.contains('schedule-focused')));
    !isClickedScheduleFocused && toggleDisplaySettings(false);
  });

  const blockColor = scheduleColors?.[shift.scheduleId];
  const { shifts } = shift;

  const toggleDisplaySettings = useCallback(
    (shouldDisplay: boolean) => {
      if (shouldDisplay === areScheduleDetailsDisplayed) return;

      return dispatch(
        toggleDisplayDetailsOnGrouppedSchedule({
          id: shift.scheduleId,
          shouldDisplay,
        }),
      );
    },
    [dispatch, areScheduleDetailsDisplayed, shift.scheduleId],
  );

  const calendarPosition = useMemo<ICalendarPosition>(
    () => getCalendarPosition(shift.displayBlocks.startBlock, shift.displayBlocks.endBlock, timezoneName, day),
    [day, shift.displayBlocks.endBlock, shift.displayBlocks.startBlock, timezoneName],
  );

  const blockSettings = useMemo<IBlockSettings>(
    () => getBlockSettings(shift.itemData.startBlock, shift.itemData.endBlock, timezoneName, day),
    [day, timezoneName, shift.itemData.endBlock, shift.itemData.startBlock],
  );

  const isOverlap = useMemo<boolean>(() => {
    if (!shifts.length || !node.current) return false;

    const label = node.current.querySelector('.schedule-labels');
    const shiftsElements = node.current.querySelectorAll('.schedule-labels ~ div');
    return [...shiftsElements].some(shift => checkOverlap(label, shift));
  }, [shifts.length]);

  // if sidebar checkbox is checked to display this schedule
  if (!selectedSchedulesIds.includes(shift.scheduleId)) {
    return <></>;
  }

  if (!calendarPosition || !blockColor) {
    return <></>;
  }

  const scheduleIndex = schedulesList.findIndex(schedule => schedule.scheduleId === shift.scheduleId) + 1;

  return (
    <ShiftTooltip
      canShow={calendarPosition.blockDuration > HOUR_IN_PX}
      shiftData={{ id: shift.itemData.id, scheduleName: shift.scheduleName, clientName: shift.clientName }}
      startBlockId={shift.originalStartBlock ?? shift.itemData.startBlock}
      endBlockId={shift.originalEndBlock ?? shift.itemData.endBlock}
      scheduleIndex={scheduleIndex}
      day={day}
    >
      <Schedule
        minutesFromMidnight={calendarPosition.minutesFromMidnight}
        blockDuration={calendarPosition.blockDuration}
        onClick={() => toggleDisplaySettings(true)}
        blockColor={blockColor}
        ref={node}
        focused={areScheduleDetailsDisplayed}
        className={classList(
          areScheduleDetailsDisplayed && 'schedule-focused',
          shift.hasVto && 'schedule-has-vto',
          shifts.some(shift => shift.shiftAssnStatus === ShiftAssignStatus.vtoAccepted) && 'schedule-has-vto-accepted',
          isOverlap && 'schedule-label-overlapped',
          Boolean(
            node.current?.querySelector('.schedule-labels + .shift-on-offer') ||
              node.current?.querySelector('.schedule-labels + .shift-vto'),
          ) && 'update-overlap-color',
          shift.splitStart && 'schedule-split-start',
          shift.splitEnd && 'schedule-split-end',
        )}
      >
        {!areScheduleDetailsDisplayed && (
          <SchedulesContainerLabels contract={shift} blockColor={blockColor} blockSettings={blockSettings} />
        )}
        <ShiftsContainer
          shifts={shifts}
          contract={shift}
          blockColor={blockColor}
          displayDetails={areScheduleDetailsDisplayed}
          modal={modal}
          onClick={onClick}
          isDlsBlocked={Boolean(shift.isShiftDlsBlocked)}
          day={day}
        />
      </Schedule>
    </ShiftTooltip>
  );
};

export { SchedulesContainer };

const Schedule = styled.div<{
  minutesFromMidnight: number;
  blockDuration: number;
  blockColor: Hue;
  focused: boolean;
}>`
  display: flex;
  flex-direction: column;

  position: absolute;
  left: 1px;

  height: 100%;
  width: calc(100% - 2px);
  padding: ${PADDING_SCHEDULE}px;

  background-color: ${props => colors[props.blockColor][props.focused ? 'c400' : 'c500']};
  border-radius: 8px;

  font-size: 10px;
  color: #fff;
  cursor: pointer;

  &.schedule-split-start {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    &:after {
      content: '';
      width: 100%;
      height: 15px;
      position: absolute;
      bottom: 0;
      left: 0;
      background-color: #fff;
      mask-image: linear-gradient(to bottom, transparent 0, #fff 15px);
      z-index: 1;
      pointer-events: none;

      .current-day & {
        background-color: ${otherColors.calendar.currentDay};
      }

      .dls-day & {
        background-color: ${otherColors.calendar.dlsDay};
      }
    }

    & > :last-child {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &.schedule-split-end {
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    &:before {
      content: '';
      width: 100%;
      height: 15px;
      position: absolute;
      top: 0;
      left: 0;
      background-color: #fff;
      mask-image: linear-gradient(to top, transparent 0, #fff 15px);
      z-index: 1;
      pointer-events: none;

      .current-day & {
        background-color: ${otherColors.calendar.currentDay};
      }

      .dls-day & {
        background-color: ${otherColors.calendar.dlsDay};
      }
    }

    & > :first-child {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }
`;
